'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: string;
  title: string;
  type: string;
  industry: string;
  description: string;
  downloadUrl: string;
  icon: string;
}

interface ResourceLibraryProps {
  resources: Resource[];
}

const ResourceLibrary: React.FC<ResourceLibraryProps> = ({ resources }) => {
  const [selectedIndustry, setSelectedIndustry] = useState<string>('All');
  const [selectedType, setSelectedType] = useState<string>('All');

  const industries = ['All', ...Array.from(new Set(resources.map(r => r.industry)))];
  const types = ['All', ...Array.from(new Set(resources.map(r => r.type)))];

  const filteredResources = resources.filter(resource => {
    const industryMatch = selectedIndustry === 'All' || resource.industry === selectedIndustry;
    const typeMatch = selectedType === 'All' || resource.type === selectedType;
    return industryMatch && typeMatch;
  });

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-4">
              <Icon name="DocumentTextIcon" size={20} className="text-accent" variant="solid" />
              <span className="text-sm font-cta font-semibold text-accent">Industry Resources</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
              Downloadable Resource Library
            </h2>
            <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
              Access industry-specific guides, compliance checklists, and technical documentation
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="flex-1">
              <label className="block text-sm font-cta font-semibold text-primary mb-2">Filter by Industry</label>
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                className="w-full px-4 py-3 bg-card border border-border rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                {industries.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-cta font-semibold text-primary mb-2">Filter by Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-4 py-3 bg-card border border-border rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                {types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => (
              <div
                key={resource.id}
                className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 p-6 space-y-4"
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name={resource.icon as any} size={24} className="text-accent" variant="solid" />
                  </div>
                  <span className="px-3 py-1 bg-muted text-xs font-cta font-semibold text-primary rounded-full">
                    {resource.type}
                  </span>
                </div>

                <div>
                  <h3 className="text-lg font-headline font-bold text-primary mb-2">{resource.title}</h3>
                  <p className="text-sm text-muted-foreground font-body mb-3">{resource.description}</p>
                  <span className="inline-block px-3 py-1 bg-brand-secondary/10 text-xs font-cta font-semibold text-brand-secondary rounded-full">
                    {resource.industry}
                  </span>
                </div>

                <button className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg font-cta font-semibold text-sm transition-all duration-300 transform hover:scale-105">
                  <Icon name="ArrowDownTrayIcon" size={18} />
                  <span>Download Resource</span>
                </button>
              </div>
            ))}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-12">
              <Icon name="FolderOpenIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground font-body">
                No resources found for the selected filters
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ResourceLibrary;